// Chart — Chart.js 4.5 wrapper for the `chart` Component type.
//
// Replaces the §5.3 stub from Phase D.0c. Implements the design-system
// rules: runtime CSS-var color binding (never hardcoded hex), Tailwind-rendered
// legend (Chart.js built-in legend disabled), FSI/PDI numeric tick isolation
// for RTL, aspect ratios per chart type, value_format-aware tick + tooltip
// formatting, doughnut center overlay via `center_label`, optional
// `highlight_index` accent for bar/pie/doughnut.
//
// **The protocol's `chartjs_config.options?: unknown` passthrough is
// deliberately ignored at this stage.** Allowing arbitrary options lets a tool
// disable our RTL/legend/tooltip defaults and break design integrity. If a
// genuine override surfaces, gate it through a typed allowlist.
//
// **Library registration is one-shot at module import** — Chart.js is global,
// not per-component. Tree-shaking keeps the bundle scoped to what we register.
//
// **Phase 2 / S10, 2026-05-16:** the protocol's chart vocabulary
// widened from `bar|line|pie|doughnut` to also include `radar`, `scatter`,
// `mixed`. This renderer continues to support the original 4 natively; the
// new 3 fall back gracefully: scatter and mixed → line, radar → bar (no
// new Chart.js plugin imports yet — `chartjs-plugin-annotation` and
// `chartjs-adapter-date-fns` deferred per that scope boundary). The
// schema is permissive so the agent doesn't have to self-correct against a
// tighter validator; the renderer degradation is invisible to the agent.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type TooltipItem,
} from 'chart.js';
// Annotation plugin (max/min labels + mean lines + threshold boxes) +
// date-fns adapter for true time-axis charts. Both deferred at close;
// installed 2026-05-18 to close the visible-rendering gap on the chart-quality
// arc.
//
// The plugin's bundle ships an internal `Chart.register(Annotation)` side-effect,
// but under Vite's ESM bundling with `chart.js` as a separate import, that
// auto-register hits a *different* Chart instance than the one this component
// imports — registered plugins ends up as `[filler, tooltip]` only. Explicit
// register via the default export attaches it to the SAME ChartJS class we use.
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-date-fns';
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';
import type { Component } from '../protocol.ts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const resolvedAnnotation: any =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (annotationPlugin as any).default ?? annotationPlugin;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  TimeScale,
  Tooltip,
  resolvedAnnotation,
);

// per-dataset color cycle for multi-series charts when the agent
// doesn't supply explicit borderColor / backgroundColor. Mirrors the slice
// palette but for line/bar datasets specifically.
const DATASET_CYCLE = (palette: Palette): string[] => [
  palette.primary,
  palette.good,
  palette.warn,
  palette.bad,
  palette.muted,
];

type ChartComp = Extract<Component, { type: 'chart' }>;

// FSI/PDI wrap for numeric labels inside an RTL chart. Hebrew tick labels
// pass through untouched (the regex only matches strings whose payload is
// digit-shaped — dates like "1.5", percentages, integers).
const FSI = '⁨';
const PDI = '⁩';
const isNumericLabel = (s: string): boolean =>
  /^[\d.,\-+%₪$]+$/.test(s.trim());
const wrapForBidi = (s: string): string =>
  isNumericLabel(s) ? `${FSI}${s}${PDI}` : s;

// Read a CSS var off the document root. Falls back to a safe neutral if the
// var isn't defined yet (early render before tokens hydrate).
const cssVar = (name: string, fallback = '#0f172a'): string => {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v.length > 0 ? v : fallback;
};

type Palette = {
  primary: string;
  primaryLight: string;
  good: string;
  warn: string;
  bad: string;
  muted: string;
  border: string;
  text: string;
};

const usePalette = (): Palette => {
  const [palette, setPalette] = useState<Palette>(() => readPalette());
  useEffect(() => {
    setPalette(readPalette());
  }, []);
  return palette;
};

const readPalette = (): Palette => ({
  primary: cssVar('--color-primary', '#2563eb'),
  primaryLight: cssVar('--color-primary-light', '#dbeafe'),
  good: cssVar('--color-good', '#10b981'),
  warn: cssVar('--color-warn', '#f59e0b'),
  bad: cssVar('--color-bad', '#ef4444'),
  muted: cssVar('--color-muted', '#64748b'),
  border: cssVar('--color-border', '#e2e8f0'),
  text: cssVar('--color-text', '#0f172a'),
});

const formatValue = (
  v: number | null | undefined,
  fmt: ChartComp['value_format'],
): string => {
  if (v == null || Number.isNaN(v)) return '';
  if (fmt === 'percent') return `${v}%`;
  if (fmt === 'currency') return `₪${v.toLocaleString('he-IL')}`;
  return v.toLocaleString('he-IL');
};

const isHighlighted = (
  index: number,
  highlight?: number | number[],
): boolean =>
  highlight != null &&
  (Array.isArray(highlight) ? highlight.includes(index) : highlight === index);

const sliceColor = (
  index: number,
  palette: Palette,
  highlight?: number | number[],
): string =>
  isHighlighted(index, highlight) ? palette.bad : palette.primary;

const PIE_CYCLE = (palette: Palette): string[] => [
  palette.primary,
  palette.good,
  palette.warn,
  palette.bad,
  palette.muted,
];

// chart-type degradation — the widened protocol accepts radar/scatter/mixed
// but this renderer hasn't bound the matching Chart.js controllers/plugins yet.
// scatter + mixed → 'line' fallback (closest visual fidelity); radar → 'bar'
// fallback (closest cartesian fallback). Original 4 types pass through.
const renderableType = (
  t: ChartComp['chartjs_config']['type'],
): 'bar' | 'line' | 'pie' | 'doughnut' => {
  if (t === 'bar' || t === 'line' || t === 'pie' || t === 'doughnut') return t;
  if (t === 'scatter' || t === 'mixed') return 'line';
  if (t === 'radar') return 'bar';
  return 'bar';
};

// Normalize dataset data to number[]. `{x, y}` rows are accepted by
// the schema; this renderer takes the `y` coordinate for the simple cartesian
// fallback. When the agent supplies `[{x:0,y:7.2}, {x:1,y:6.4}]`, the renderer
// plots [7.2, 6.4] against c.labels — which is the same visual as if the agent
// supplied [7.2, 6.4] directly.
const normalizeData = (
  raw: number[] | Array<{ x: number | string; y: number }>,
): number[] => {
  if (raw.length === 0) return [];
  // Type guard: every element is a number → already in target shape.
  if (raw.every((p): p is number => typeof p === 'number')) return raw;
  return (raw as Array<{ x: number | string; y: number }>).map((p) => p.y);
};

// `labels` widened to `string[] | {type: 'time'}`. The time-axis
// shape requires `chartjs-adapter-date-fns` (deferred per S10 scope); fall
// back to dataset indices when time is requested but plugin isn't loaded.
const normalizeLabels = (
  labels: ChartComp['labels'],
  dataLen: number,
): string[] => {
  if (Array.isArray(labels)) return labels;
  // {type: 'time'} fallback — use bare numeric indices as labels.
  return Array.from({ length: dataLen }, (_, i) => String(i));
};

// Hex `#RRGGBB` → `#RRGGBB33` (~20% alpha) for the line-fill tint.
// Accepts any CSS color string; non-hex inputs return as-is + the caller
// degrades to no fill if Chart.js doesn't recognize it.
const withFillAlpha = (color: string): string => {
  if (/^#[0-9a-f]{6}$/i.test(color)) return `${color}33`;
  if (/^#[0-9a-f]{8}$/i.test(color)) return color;
  return color;
};

const buildBarLineData = (
  c: ChartComp,
  palette: Palette,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ChartData<'bar' | 'line', any[], string> => {
  const isLine = renderableType(c.chartjs_config.type) === 'line';
  const isTimeAxis = !Array.isArray(c.labels) && c.labels?.type === 'time';
  const cycle = DATASET_CYCLE(palette);
  // Iterate ALL datasets (was index-0 only). Each dataset's
  // agent-supplied borderColor / backgroundColor wins when present;
  // fallback walks the cycle so multi-series gets distinct colors.
  // Single-dataset bar charts keep the per-slice color shape via
  // sliceColor + highlight_index. When labels:{type:'time'}, pass the
  // raw {x,y} points through (chartjs-adapter-date-fns parses x).
  const datasets = (c.datasets ?? []).map((dsRaw, dsIdx) => {
    const data = isTimeAxis ? dsRaw.data : normalizeData(dsRaw.data);
    const cycleColor = cycle[dsIdx % cycle.length] ?? palette.primary;
    const sliceColors =
      !isLine && (c.datasets?.length ?? 0) === 1
        ? (data as number[]).map((_, i) =>
            sliceColor(i, palette, c.highlight_index),
          )
        : null;
    const borderColor = dsRaw.borderColor ?? (sliceColors ?? cycleColor);
    const backgroundColor =
      dsRaw.backgroundColor ??
      (isLine
        ? withFillAlpha(typeof borderColor === 'string' ? borderColor : cycleColor)
        : sliceColors ?? cycleColor);
    return {
      label: dsRaw.label,
      data,
      backgroundColor,
      borderColor,
      borderWidth: 2,
      ...(dsRaw.type ? { type: dsRaw.type } : {}),
      ...(dsRaw.yAxisID ? { yAxisID: dsRaw.yAxisID } : {}),
      ...(isLine
        ? { tension: 0.3, fill: true, pointRadius: 4, pointHoverRadius: 6 }
        : { borderRadius: 6 }),
    };
  });
  // Empty-datasets safety: keep the legacy single-empty-dataset shape so
  // downstream Chart.js render doesn't crash on an empty array.
  if (datasets.length === 0) {
    return {
      labels: isTimeAxis ? [] : normalizeLabels(c.labels, 0),
      datasets: [
        {
          label: '',
          data: [],
          backgroundColor: isLine ? `${palette.primary}33` : palette.primary,
          borderColor: palette.primary,
          borderWidth: 2,
        },
      ],
    };
  }
  // time-axis charts use the data point's `x` as the time value;
  // labels array is empty (Chart.js infers ticks from data).
  if (isTimeAxis) {
    return { labels: [], datasets };
  }
  const firstData = normalizeData(c.datasets[0]?.data ?? []);
  return {
    labels: normalizeLabels(c.labels, firstData.length),
    datasets,
  };
};

const buildPieDoughnutData = (
  c: ChartComp,
  palette: Palette,
): ChartData<'pie' | 'doughnut', number[], string> => {
  const dsRaw = c.datasets[0] ?? { label: '', data: [] };
  const data = normalizeData(dsRaw.data);
  const cycle = PIE_CYCLE(palette);
  const colors = data.map((_, i) =>
    isHighlighted(i, c.highlight_index) ? palette.bad : cycle[i % cycle.length],
  );
  return {
    labels: normalizeLabels(c.labels, data.length),
    datasets: [
      {
        label: dsRaw.label,
        data,
        backgroundColor: colors,
        borderColor: palette.border,
        borderWidth: 2,
      },
    ],
  };
};

// Preload HTMLImageElement instances for any URLs that appear on
// `chart.center_image` or label annotation `image`. Returns a map of
// loaded images keyed by URL. Images that fail to load are simply omitted;
// the chart degrades to text content for label annotations or no overlay
// for center_image. Lives inline (no separate file) — the lifecycle is
// keyed to the chart prop `c` and resets when component-c changes.
const useLoadedImages = (urls: string[]): Map<string, HTMLImageElement> => {
  const [loaded, setLoaded] = useState<Map<string, HTMLImageElement>>(
    () => new Map(),
  );
  // Stable key so React doesn't re-run on shallow array-identity changes
  // while the URL list is the same. Sorted to make permutation no-op.
  const key = useMemo(() => urls.slice().sort().join('|'), [urls]);
  useEffect(() => {
    if (urls.length === 0) {
      setLoaded(new Map());
      return;
    }
    let cancelled = false;
    const next = new Map<string, HTMLImageElement>();
    let remaining = urls.length;
    const settle = () => {
      if (cancelled) return;
      // Re-render once each URL has either loaded or errored. Errored
      // images are simply absent from the map; consumers treat that as
      // "not available" and degrade.
      setLoaded(new Map(next));
    };
    for (const url of urls) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        next.set(url, img);
        remaining -= 1;
        if (remaining === 0) settle();
      };
      img.onerror = () => {
        remaining -= 1;
        if (remaining === 0) settle();
      };
      img.src = url;
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return loaded;
};

// Collect every URL the chart wants to render as an image, so the
// preloader has one place to call. Both center_image (absolute overlay)
// and label-annotation image (drawn inside the canvas) flow through this.
const collectImageUrls = (c: ChartComp): string[] => {
  const out = new Set<string>();
  if (c.center_image && /^https?:\/\//.test(c.center_image)) {
    out.add(c.center_image);
  }
  for (const a of c.annotations ?? []) {
    if (a.type === 'label' && a.image && /^https?:\/\//.test(a.image)) {
      out.add(a.image);
    }
  }
  return [...out];
};

// An annotation photo is drawn at most this share of the plot's shorter side
// on its longer side, so it never covers the data.
const PHOTO_MAX_SHARE = 0.3;

// Drawn width or height of an annotation photo: the natural size scaled down
// (never up) so the longer side is at most PHOTO_MAX_SHARE of the plot's
// shorter side, keeping the aspect ratio. Resolved by the plugin at draw time,
// when the chart's real size is known.
const boundedPhotoSide = (
  chart: ChartJS,
  img: HTMLImageElement,
  side: 'width' | 'height',
): number => {
  const area = chart.chartArea;
  const plotW = area ? area.right - area.left : chart.width;
  const plotH = area ? area.bottom - area.top : chart.height;
  const bound = PHOTO_MAX_SHARE * Math.min(plotW, plotH);
  const scale = Math.min(1, bound / Math.max(img.width, img.height));
  return Math.round(img[side] * scale);
};

// Offset that moves a photo's centre inward just far enough to keep the whole
// photo inside the chart area; 0 when it already fits (or the chart has no
// x/y scales, as on pie and doughnut). The photo is centred on its point, so a
// point on the plot's edge (the first and last of a line chart) would
// otherwise be clipped to half.
const photoAdjust = (
  chart: ChartJS,
  img: HTMLImageElement,
  axis: 'x' | 'y',
  value: number | string,
): number => {
  const area = chart.chartArea;
  const scale = chart.scales[axis];
  if (!area || !scale) return 0;
  // Parse through the scale first, as the plugin's own positioning does: a
  // time axis holds a date string as a timestamp, a category axis a label as
  // an index; the raw value would give NaN there.
  const parsed = typeof value === 'number' ? value : scale.parse(value);
  // A value the scale cannot place has no point to keep inside; the plugin
  // then positions the label by its own fallback, so no adjustment applies.
  if (typeof parsed !== 'number' || !Number.isFinite(parsed)) return 0;
  const centre = scale.getPixelForValue(parsed);
  const half =
    boundedPhotoSide(chart, img, axis === 'x' ? 'width' : 'height') / 2;
  const [lo, hi] =
    axis === 'x' ? [area.left, area.right] : [area.top, area.bottom];
  return Math.min(Math.max(centre, lo + half), hi - half) - centre;
};

// Translate the agent's annotation array (protocol shape) into the
// chartjs-plugin-annotation keyed-object shape. Per-type fixups:
//
//   {type:'line',  value:6.26, scaleID:'y'}                 → {type:'line',  yMin:6.26, yMax:6.26, scaleID:'y'}
//                                                            (x-scale uses xMin/xMax)
//   {type:'label', xValue:'27/4', yValue:11, content:'מקס:11'} → passthrough (plugin-native)
//   {type:'box',   xMin, xMax, yMin, yMax}                  → passthrough
//   {type:'point', xValue, yValue}                          → passthrough
//
// The protocol's `value: number|string` for a line annotation is interpreted
// as the constant along the scaleID axis (`'y'` / `'y1'` / `'y2'` → horizontal
// line; `'x'` → vertical line). Returns undefined when there's nothing to
// render, so the caller can skip the plugin block entirely.
export const buildAnnotations = (
  c: ChartComp,
  palette: Palette,
  images: Map<string, HTMLImageElement>,
): Record<string, object> | undefined => {
  const anns = c.annotations;
  if (!anns || anns.length === 0) return undefined;
  const out: Record<string, object> = {};
  anns.forEach((a, i) => {
    const id = `a${i}`;
    if (a.type === 'line') {
      const scaleID = a.scaleID ?? 'y';
      const value = typeof a.value === 'number' ? a.value : Number(a.value);
      const isXScale = scaleID === 'x';
      out[id] = {
        type: 'line',
        scaleID,
        ...(isXScale
          ? { xMin: value, xMax: value }
          : { yMin: value, yMax: value }),
        borderColor: a.borderColor ?? palette.muted,
        borderWidth: 2,
        borderDash: [6, 4],
      };
    } else if (a.type === 'label') {
      // When the agent supplied `image` and the URL is loaded,
      // swap `content` for the HTMLImageElement. The plugin draws the
      // image at xValue/yValue; without a width/height it would use the
      // image's natural size and cover the chart, so each side is bounded
      // against the plot at draw time (see `boundedPhotoSide`) and the photo
      // is kept wholly inside the chart area (see `photoAdjust`). If the
      // image hasn't loaded yet (or failed), degrade to the text content
      // the agent also supplied.
      const loadedImage =
        a.image && /^https?:\/\//.test(a.image) ? images.get(a.image) : null;
      const content = loadedImage
        ? loadedImage
        : Array.isArray(a.content)
          ? a.content
          : [a.content];
      out[id] = loadedImage
        ? {
            // Image content — strip background/color (the image is opaque).
            type: 'label',
            xValue: a.xValue,
            yValue: a.yValue,
            content,
            width: (ctx: { chart: ChartJS }) =>
              boundedPhotoSide(ctx.chart, loadedImage!, 'width'),
            height: (ctx: { chart: ChartJS }) =>
              boundedPhotoSide(ctx.chart, loadedImage!, 'height'),
            xAdjust: (ctx: { chart: ChartJS }) =>
              photoAdjust(ctx.chart, loadedImage!, 'x', a.xValue),
            yAdjust: (ctx: { chart: ChartJS }) =>
              photoAdjust(ctx.chart, loadedImage!, 'y', a.yValue),
          }
        : {
            type: 'label',
            xValue: a.xValue,
            yValue: a.yValue,
            content,
            backgroundColor: a.backgroundColor ?? '#0f172a',
            color: a.color ?? '#fff',
          };
    } else if (a.type === 'box') {
      out[id] = {
        type: 'box',
        xMin: a.xMin,
        xMax: a.xMax,
        yMin: a.yMin,
        yMax: a.yMax,
        backgroundColor: a.backgroundColor ?? `${palette.warn}22`,
        borderColor: a.borderColor ?? palette.warn,
        borderWidth: 1,
      };
    } else if (a.type === 'point') {
      out[id] = {
        type: 'point',
        xValue: a.xValue,
        yValue: a.yValue,
        backgroundColor: a.backgroundColor ?? palette.bad,
        radius: 5,
      };
    }
  });
  return Object.keys(out).length > 0 ? out : undefined;
};

const baseOptions = (
  c: ChartComp,
  palette: Palette,
  images: Map<string, HTMLImageElement>,
): ChartOptions<'bar' | 'line' | 'pie' | 'doughnut'> => {
  const annotations = buildAnnotations(c, palette, images);
  return {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
        backgroundColor: palette.text,
        bodyColor: '#fff',
        titleColor: '#fff',
        callbacks: {
          label: (ctx: TooltipItem<'bar' | 'line' | 'pie' | 'doughnut'>) => {
            const raw = ctx.parsed as number | { y?: number } | null;
            const value =
              typeof raw === 'number'
                ? raw
                : raw && 'y' in raw && typeof raw.y === 'number'
                  ? raw.y
                  : null;
            const formatted = formatValue(value, c.value_format);
            const label = ctx.label ?? '';
            return wrapForBidi(`${label} · ${formatted}`);
          },
        },
      },
      // Only inject the annotation plugin config when the agent
      // actually supplied annotations — keeps the empty-options object clean.
      ...(annotations ? { annotation: { annotations } } : {}),
    },
  };
};

const cartesianOptions = (
  c: ChartComp,
  palette: Palette,
  images: Map<string, HTMLImageElement>,
): ChartOptions<'bar' | 'line'> => {
  // When the agent supplies labels:{type:'time'}, switch the x-axis to
  // time scale (chartjs-adapter-date-fns parses the `x` values in each
  // dataset.data point). Category scale remains the default when labels is
  // string[].
  const isTimeAxis = !Array.isArray(c.labels) && c.labels?.type === 'time';
  const labelArr = Array.isArray(c.labels) ? c.labels : [];
  return {
    ...(baseOptions(c, palette, images) as ChartOptions<'bar' | 'line'>),
    scales: {
      x: isTimeAxis
        ? {
            type: 'time',
            time: { unit: 'day', tooltipFormat: 'yyyy-MM-dd' },
            ticks: { color: palette.muted },
            grid: { display: false },
          }
        : {
            ticks: {
              color: palette.muted,
              callback(_value, index) {
                const lbl = labelArr[index] ?? '';
                return wrapForBidi(lbl);
              },
            },
            grid: { display: false },
          },
      y: {
        beginAtZero: true,
        ticks: {
          color: palette.muted,
          callback(value) {
            const n = typeof value === 'number' ? value : Number(value);
            return wrapForBidi(formatValue(n, c.value_format));
          },
        },
        grid: { color: palette.border },
      },
      // widened ChartComponent allows yAxisID 'y1' / 'y2' on
      // individual datasets. Declare them with display=false by default
      // (they're auto-added by Chart.js when a dataset references them);
      // explicit declaration here gives Chart.js a hint to position right.
      y1: {
        type: 'linear',
        position: 'right',
        display: 'auto',
        grid: { drawOnChartArea: false },
        ticks: { color: palette.muted },
      },
      y2: {
        type: 'linear',
        position: 'right',
        display: 'auto',
        grid: { drawOnChartArea: false },
        ticks: { color: palette.muted },
      },
    },
  };
};

const aspectClass = (type: ChartComp['chartjs_config']['type']): string => {
  // Degraded types (radar/scatter/mixed) follow their cartesian fallback.
  const renderable = renderableType(type);
  if (renderable === 'bar' || renderable === 'line') return 'aspect-[16/10]';
  return 'aspect-square max-w-[260px] mx-auto';
};

const Legend = ({ c, palette }: { c: ChartComp; palette: Palette }) => {
  const type = renderableType(c.chartjs_config.type);
  // Labels can be `{type: 'time'}` — fall back to empty list for legend.
  const labelArr = Array.isArray(c.labels) ? c.labels : [];
  if (type === 'pie' || type === 'doughnut') {
    const cycle = PIE_CYCLE(palette);
    return (
      <ul className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-[var(--color-muted)]">
        {labelArr.map((label, i) => {
          const color = isHighlighted(i, c.highlight_index)
            ? palette.bad
            : cycle[i % cycle.length];
          return (
            <li key={`${label}-${i}`} className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: color }}
                aria-hidden="true"
              />
              <span>{label}</span>
            </li>
          );
        })}
      </ul>
    );
  }
  // multi-dataset legend — show one chip per dataset, with the
  // agent-supplied borderColor when present (falls back to the dataset cycle).
  const datasets = c.datasets ?? [];
  const labeled = datasets.filter((d) => d.label);
  if (labeled.length === 0) return null;
  const cycle = DATASET_CYCLE(palette);
  return (
    <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-[var(--color-muted)]">
      {labeled.map((ds, i) => {
        const idx = datasets.indexOf(ds);
        const color =
          (typeof ds.borderColor === 'string' ? ds.borderColor : undefined) ??
          cycle[idx % cycle.length] ??
          palette.primary;
        return (
          <span
            key={`${ds.label}-${i}`}
            className="inline-flex items-center gap-1.5"
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: color }}
              aria-hidden="true"
            />
            <span>{ds.label}</span>
          </span>
        );
      })}
    </div>
  );
};

export const Chart = ({ c }: { c: ChartComp }) => {
  const palette = usePalette();
  const containerRef = useRef<HTMLDivElement>(null);
  // Collect image URLs (center_image + annotation.image) so they
  // can be preloaded; pass the loaded map into option-builders.
  const imageUrls = useMemo(() => collectImageUrls(c), [c]);
  const loadedImages = useLoadedImages(imageUrls);

  const cartesianData = useMemo(
    () => buildBarLineData(c, palette),
    [c, palette],
  );
  const radialData = useMemo(
    () => buildPieDoughnutData(c, palette),
    [c, palette],
  );
  const cartOpts = useMemo(
    () => cartesianOptions(c, palette, loadedImages),
    [c, palette, loadedImages],
  );
  const radialOpts = useMemo(
    () => baseOptions(c, palette, loadedImages) as ChartOptions<'pie' | 'doughnut'>,
    [c, palette, loadedImages],
  );

  // Use the renderable fallback for the actual switch — radar/scatter/mixed
  // degrade to their closest supported visual. aspectClass uses the same mapping.
  const type = renderableType(c.chartjs_config.type);

  // center_image absolute overlay (mirrors center_label at L598).
  // Only show once the image is actually loaded — guards against blank
  // <img> during the preload, and against an erroring URL (loadedImages
  // doesn't carry failed loads). Doughnut-only by design.
  const centerImageUrl =
    c.center_image && /^https?:\/\//.test(c.center_image)
      ? loadedImages.get(c.center_image)?.src
      : null;

  return (
    <div className="mt-2 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-sm shadow-[var(--shadow-bubble)]">
      <div className="font-bold mb-2">{c.title}</div>
      <div ref={containerRef} className={`relative w-full ${aspectClass(c.chartjs_config.type)}`}>
        {type === 'bar' ? (
          <Bar
            data={cartesianData as ChartData<'bar', number[], string>}
            options={cartOpts as ChartOptions<'bar'>}
          />
        ) : type === 'line' ? (
          <Line
            data={cartesianData as ChartData<'line', number[], string>}
            options={cartOpts as ChartOptions<'line'>}
          />
        ) : type === 'pie' ? (
          <Pie
            data={radialData as ChartData<'pie', number[], string>}
            options={radialOpts as ChartOptions<'pie'>}
          />
        ) : (
          <>
            <Doughnut
              data={radialData as ChartData<'doughnut', number[], string>}
              options={radialOpts as ChartOptions<'doughnut'>}
            />
            {centerImageUrl ? (
              // Center artwork — sized to ~35% of the chart's
              // shortest dimension via the parent's aspect-square + a
              // simple percentage. `object-contain` preserves aspect.
              // `pointer-events-none` so the overlay doesn't steal hover
              // from the canvas tooltip layer.
              <div
                className="pointer-events-none absolute inset-0 flex items-center justify-center"
                aria-hidden="true"
              >
                <img
                  src={centerImageUrl}
                  alt=""
                  className="h-[35%] w-[35%] rounded-full object-cover"
                />
              </div>
            ) : null}
            {c.center_label ? (
              <div
                className="pointer-events-none absolute inset-0 flex items-center justify-center text-base font-bold tabular-nums text-[var(--color-text)]"
                aria-hidden="true"
              >
                {wrapForBidi(c.center_label)}
              </div>
            ) : null}
          </>
        )}
      </div>
      <Legend c={c} palette={palette} />
    </div>
  );
};
